/**
 * หมวดของไอเทม ใช้ร่วมกันระหว่างหน้าต่างกระเป๋าและร้านค้า
 *
 * - แท็บหลักแยกตามประเภท (ใช้ได้ · อุปกรณ์ · วัตถุดิบ · การ์ด) และมีแท็บ "ทั้งหมด"
 * - อุปกรณ์กรองต่อได้ตามชนิด (อาวุธ เสื้อ ...) และตามอาชีพที่ใส่ได้
 *
 * แค่จัดหมวดเพื่อ "แสดง" ไม่ได้ตัดสินว่าใส่ได้ไหม (server ตรวจเองตอนสวม)
 * pure function ไม่รู้จัก DOM จึงเทสต์ได้ด้วยข้อมูลจาก items.json ตรง ๆ
 */

import type { EquipKind, ItemDef } from "@wickhaven/shared";

export type BagTab = "all" | "use" | "equip" | "material" | "card";

export const BAG_TABS: ReadonlyArray<{ id: BagTab; label: string }> = [
  { id: "all", label: "ทั้งหมด" },
  { id: "use", label: "ใช้ได้" },
  { id: "equip", label: "อุปกรณ์" },
  { id: "material", label: "วัตถุดิบ" },
  { id: "card", label: "ไฟตะเกียง" },
];

/** แท็บของไอเทมชิ้นนี้ (ไม่มีวันเป็น "all") */
export function tabOf(def: ItemDef | undefined): Exclude<BagTab, "all"> {
  switch (def?.kind) {
    case "heal": return "use";
    case "equip": return "equip";
    case "card": return "card";
    default: return "material";
  }
}

export type GearType =
  | "all" | "weapon" | "offhand" | "head" | "body" | "legs" | "feet" | "accessory" | "wings" | "pet";

/** ชนิดอุปกรณ์ที่กรองได้ ชนิดช่องที่ใกล้กันรวมกลุ่มเดียว (หมวกกับของบนหน้า · ต่างหูกับเครื่องประดับพิเศษ) */
export const GEAR_TYPES: ReadonlyArray<{ id: GearType; label: string; kinds: readonly EquipKind[] }> = [
  { id: "all", label: "ทุกชนิด", kinds: [] },
  { id: "weapon", label: "อาวุธ", kinds: ["weapon"] },
  { id: "offhand", label: "โล่", kinds: ["offhand"] },
  { id: "head", label: "หมวก", kinds: ["head", "face"] },
  { id: "body", label: "เสื้อ", kinds: ["body"] },
  { id: "legs", label: "กางเกง", kinds: ["legs"] },
  { id: "feet", label: "รองเท้า", kinds: ["feet"] },
  { id: "accessory", label: "เครื่องประดับ", kinds: ["ear", "special"] },
  { id: "wings", label: "ปีก", kinds: ["wings"] },
  { id: "pet", label: "สัตว์เลี้ยง", kinds: ["pet"] },
];

/** ตัวกรองอาชีพ "all" = ทุกอาชีพ · "mine" = ใส่ได้กับอาชีพของเรา · หรือ id อาชีพ */
export type ClassFilter = "all" | "mine" | string;

export interface ItemFilter {
  tab: BagTab;
  gear: GearType;
  cls: ClassFilter;
}

export const DEFAULT_FILTER: ItemFilter = { tab: "all", gear: "all", cls: "all" };

/** อาชีพนี้ใส่ของชิ้นนี้ได้ไหม (ไม่ระบุ reqClass = ทุกอาชีพ) */
export function fitsClass(def: ItemDef, cls: string): boolean {
  return !def.reqClass || def.reqClass.length === 0 || def.reqClass.includes(cls);
}

/** ไอเทมชิ้นนี้ผ่านตัวกรองไหม `myCls` ใช้กับตัวกรอง "mine" */
export function matchFilter(def: ItemDef | undefined, f: ItemFilter, myCls: string): boolean {
  if (!def) return f.tab === "all" || f.tab === "material";
  if (f.tab !== "all" && tabOf(def) !== f.tab) return false;
  // ตัวกรองชนิดและอาชีพมีผลเฉพาะอุปกรณ์ ของอย่างอื่นผ่านไปเลยในแท็บทั้งหมด
  if (def.kind !== "equip") return f.tab !== "equip";
  if (f.gear !== "all") {
    const kinds = GEAR_TYPES.find((g) => g.id === f.gear)?.kinds ?? [];
    if (!def.slot || !kinds.includes(def.slot)) return false;
  }
  if (f.cls === "mine") return fitsClass(def, myCls);
  if (f.cls !== "all") return fitsClass(def, f.cls);
  return true;
}

/** ลำดับแสดงผล: ของใช้ก่อน แล้วอุปกรณ์ วัตถุดิบ การ์ด ภายในหมวดเรียงตาม id */
export const TAB_ORDER: Readonly<Record<Exclude<BagTab, "all">, number>> = {
  use: 0, equip: 1, material: 2, card: 3,
};

export function sortItems<T extends { id: string; def: ItemDef | undefined }>(list: T[]): T[] {
  return list.sort((a, b) =>
    TAB_ORDER[tabOf(a.def)] - TAB_ORDER[tabOf(b.def)] || a.id.localeCompare(b.id));
}
